import React from "react";
import { CreditCard } from "lucide-react";
import Button from "../../../components/Buttons/Button/Button";
import ButtonClose from "../../../components/Buttons/ButtonClose/ButtonClose";
import "./ReviewChangesModal.css";

function ReviewChangesModal({
  open,
  t,
  currentTotal,
  newTotal,
  mode = "change",
  billing,
  onBillingChange,
  showBillingSelector,
  billingMonthlyPrice,
  billingYearlyPrice,
  billingCurrency,
  billingMonthlyDisabled,
  billingYearlyDisabled,
  error,
  loading,
  completed,
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  const normalizeError = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value?.message === "string") return value.message;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const rawError = normalizeError(error);

  const getFriendlyError = (raw) => {
    const text = (raw || "").toLowerCase();
    if (!text) return null;

    if (text.includes("from_price_id") && text.includes("not found")) {
      return {
        message:
          t("page.errorUpdatePlan.message"),
      };
    }

    if (text.includes("internal server error") || text.includes("server error")) {
      return {
        message: t("page.errorUpdatePlan.message"),
      };
    }

    if (text.includes("network") || text.includes("timeout")) {
      return {
        message: t("page.errorUpdatePlan.message"),
      };
    }

    return {
      // Si el backend envía un mensaje concreto (por ejemplo,
      // "Organization already has an active Stripe subscription"),
      // lo mostramos tal cual para que el usuario entienda el motivo.
      message: raw,
    };
  };

  const friendlyError = rawError ? getFriendlyError(rawError) : null;

  const formatMoney = (value) => {
    if (typeof value !== "number") return "-";
    return `${billingCurrency || "USD"} ${value.toFixed(2)}`;
  };

  return (
    <div className="suscriptions-modal-overlay" role="dialog" aria-modal="true">
      <div className="suscriptions-modal-dialog">
        <div className="suscriptions-modal-header">
          <div className="suscriptions-modal-title-section">
            <CreditCard size={20} aria-hidden="true" stroke="var(--primary)" />
            <h3 className="suscriptions-modal-title">
              {mode === "create"
                ? t("page.reviewCreate.title")
                : t("page.reviewConfirm.title")}
            </h3>
          </div>
          <ButtonClose
            onClick={onClose}
            ariaLabel={t("page.reviewConfirm.back")}
            disabled={loading}
          />
        </div>
        <p className="suscriptions-modal-message">
          {mode === "create"
            ? t("page.reviewCreate.message", {
              next: newTotal.toFixed(2),
            })
            : t("page.reviewConfirm.message", {
              current: currentTotal.toFixed(2),
              next: newTotal.toFixed(2),
            })}
        </p>

        {(loading || completed) && (
          <div className="suscriptions-modal-status suscriptions-modal-status-centered">
            <span
              className={
                "suscriptions-modal-status-icon " +
                (completed ? "suscriptions-modal-status-icon-success" : "suscriptions-modal-status-icon-spinning")
              }
              aria-hidden="true"
            >
              {completed ? <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="64"
                height="64"
                fill="none"
                stroke="#2ecc71"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="5 13 10 18 19 7" />
              </svg>
                : <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="64"
                  height="64"
                  fill="none"
                  stroke="#2ecc71"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="12" x2="15" y2="12" />
                </svg>

              }
            </span>
            <span className="suscriptions-modal-status-text">
              {completed
                ? t("page.reviewConfirm.doneMessage")
                : mode === "create"
                  ? t("page.reviewCreate.loading")
                  : t("page.reviewConfirm.loading")}
            </span>
          </div>
        )}

        {showBillingSelector ? (
          <div className="suscriptions-modal-billing">
            <div className="suscriptions-modal-billing-title">
              {t("page.table.period")}
            </div>
            <div className="suscriptions-modal-billing-options">
              <label className="suscriptions-modal-billing-option">
                <input
                  type="radio"
                  name="billing"
                  checked={(billing || "monthly") === "monthly"}
                  disabled={loading || billingMonthlyDisabled}
                  onChange={() => onBillingChange?.("monthly")}
                />
                <span className="suscriptions-modal-billing-option-content">
                  <span className="suscriptions-modal-billing-option-label">{t("page.billing.monthly")}</span>
                  <span className="suscriptions-modal-billing-option-price">
                    {formatMoney(billingMonthlyPrice)}
                  </span>
                </span>
              </label>

              <label className="suscriptions-modal-billing-option">
                <input
                  type="radio"
                  name="billing"
                  checked={(billing || "monthly") === "yearly"}
                  disabled={loading || billingYearlyDisabled}
                  onChange={() => onBillingChange?.("yearly")}
                />
                <span className="suscriptions-modal-billing-option-content">
                  <span className="suscriptions-modal-billing-option-label">{t("page.billing.yearly")}</span>
                  <span className="suscriptions-modal-billing-option-price">
                    {formatMoney(billingYearlyPrice)}
                  </span>
                </span>
              </label>
            </div>
          </div>
        ) : null}

        {rawError ? (
          <div className="suscriptions-modal-error">
            <div className="suscriptions-modal-error-header">
              <span className="suscriptions-modal-error-icon" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="64"
                  height="64"
                  fill="none"
                  stroke="#e74c3c"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>

              </span>
              <div>
                <div className="suscriptions-modal-error-title">{t("page.errorUpdatePlan.title")}</div>
                <div className="suscriptions-modal-error-message">{friendlyError?.message}</div>
              </div>
            </div>
          </div>
        ) : null}

        {!loading && !completed && (
          <div className="suscriptions-modal-actions">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onClose}
            >
              {t("page.reviewConfirm.back")}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onConfirm}
            >
              {t("page.reviewConfirm.confirm")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewChangesModal;
